import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Edit2, Plus, ArrowRight } from 'lucide-react';

interface SelectedOffer {
  id: string;
  title: string;
  category: string;
  quantity: number;
  price: number;
  unitType: string;
  notes?: string;
}

interface CategorySummaryStepProps {
  selectedOffers: SelectedOffer[];
  processedCategories: string[];
  onComplete: () => void;
  onModifyCategory: (category: string) => void;
  onAddCategory: () => void;
}

export const CategorySummaryStep = ({ 
  selectedOffers, 
  processedCategories,
  onComplete, 
  onModifyCategory,
  onAddCategory 
}: CategorySummaryStepProps) => {
  // Grouper les offres par catégorie
  const offersByCategory = selectedOffers.reduce((acc, offer) => {
    if (!acc[offer.category]) {
      acc[offer.category] = [];
    }
    acc[offer.category].push(offer);
    return acc;
  }, {} as Record<string, SelectedOffer[]>);

  // Calculer les totaux
  const categoryTotals = Object.entries(offersByCategory).map(([category, offers]) => ({
    category,
    offers,
    total: offers.reduce((sum, offer) => sum + (offer.price * offer.quantity), 0)
  }));

  const grandTotal = categoryTotals.reduce((sum, cat) => sum + cat.total, 0);

  return (
    <div className="space-y-6 p-4">
      <div className="mb-6">
        <h3 className="font-semibold text-lg mb-2">Récapitulatif par catégorie</h3>
        <p className="text-sm text-muted-foreground">
          Voici l'ensemble de votre sélection organisée par catégorie.
        </p>
      </div>

      {/* Récapitulatif par catégorie */}
      <div className="space-y-4">
        {categoryTotals.map(({ category, offers, total }) => (
          <Card key={category}>
            <CardHeader className="pb-3">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <CardTitle className="text-base">{category}</CardTitle>
                  <Badge variant="secondary">
                    {offers.length} offre{offers.length > 1 ? 's' : ''}
                  </Badge>
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onModifyCategory(category)}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="pt-0">
              <div className="space-y-2">
                {offers.map((offer) => (
                  <div key={offer.id} className="flex justify-between items-center text-sm">
                    <div className="flex items-center gap-2">
                      <span>{offer.title}</span>
                      <Badge variant="outline" className="text-xs">
                        x{offer.quantity}
                      </Badge>
                    </div>
                    <span className="text-muted-foreground">Sur-mesure</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Total retiré — proposition sur-mesure établie par le chef de projet */}
      <div className="flex items-center justify-center p-3 bg-primary/5 rounded-lg border border-primary/15">
        <span className="text-sm text-muted-foreground text-center">
          Les tarifs vous seront précisés dans votre proposition sur-mesure.
        </span>
      </div>

      {/* Actions */}
      <div className="space-y-3">
        <Button 
          onClick={onAddCategory}
          variant="outline"
          className="w-full flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Ajouter une catégorie
        </Button>
        
        <Button 
          onClick={onComplete}
          className="w-full flex items-center justify-center gap-2"
        >
          <ArrowRight className="w-4 h-4" />
          Continuer (vers date et lieu)
        </Button>
      </div>

      <div className="text-center text-sm text-muted-foreground">
        Vous pourrez affiner les détails dans les étapes suivantes.
      </div>
    </div>
  );
};